import React, { useState } from 'react';
import { useFormik } from 'formik';
import { useDispatch } from 'react-redux';
import { Constants } from 'core';
import { Checkbox, Typography, Tooltip } from 'antd';
import styled from 'styled-components';
import { CalendarOutlined, FileExcelOutlined } from '@ant-design/icons';
import { path } from 'ramda';
import moment from 'moment';

import Input from 'components/organisms/input';
import Button from 'components/molecules/button';
import { historicCommunicationActions } from 'common/config/store';
import { useIntl } from 'common/config/i18n';
import { StyledBlueLink } from 'common/styling/mixins';
import FiltersCard from 'components/organisms/filters-card';
import InputSelect from 'components/organisms/select';
import DatePickerForMomentDates from 'components/organisms/datepicker';

import { formatDate, FormValues, Props, shoudHaveDates, useRefreshEffect } from './utils';

const { Paragraph } = Typography;

const UpLabel = styled.div`
  font-size: ${path(['theme', 'font', 'size', 'body'])};
  font-weight: bold;
`;

const Form = (props: Props & FormValues) => {
  const { t, currentLocale } = useIntl();
  const dispatch = useDispatch();

  const [isNeedsDates, setIsNeedsDates] = useState<boolean>(false);

  const initialValues: FormValues = {
    isDifferedCommunication: false,
    serviceName: props.circulationValues?.serviceName || '',
    station: '',
    communicationStartDate: props.circulationValues?.circulationDate
      ? undefined
      : moment().subtract(10, 'days').format(),
    communicationEndDate: props.circulationValues?.circulationDate ? undefined : moment().format(),
    departureTime: props.circulationValues?.circulationDate || '',
    arrivalTime: props.circulationValues?.circulationDate || '',
    situationIds: [],
    sender: '',
    sendStatus: undefined,
    serviceTypeIDs: undefined,
  };

  const formik = useFormik({
    initialValues,
    onSubmit: (values) => {
      props.setFilterValues(values);
      if (shoudHaveDates(values)) {
        setIsNeedsDates(true);
      } else {
        dispatch(
          historicCommunicationActions.fetchHistoricCommunication({
            ...values,
            communicationStartDate: formatDate(values.communicationStartDate),
            communicationEndDate: formatDate(values.communicationEndDate),
            departureTime: formatDate(values.departureTime),
            arrivalTime: formatDate(values.arrivalTime),
            parameterToSort: 'SentDateTime',
            ascDesc: 'DESC',
            pageNumber: 1,
            maxResultPage: props.data?.maxResultPage,
          })
        );
      }
    },
  });

  useRefreshEffect(
    formik.handleSubmit,
    {
      pageNumber: props.data?.pageNumber || 1,
      isModalVisible: props.isCommunicationHistoryModalVisible,
    },
    Constants.HISTORISATION_REFRESH_DELAY
  );

  const exportHistoricCommunication = () => {
    if (shoudHaveDates(formik.values)) {
      setIsNeedsDates(true);
    } else {
      dispatch(
        historicCommunicationActions.exportHistoricCommunication(
          {
            ...formik.values,
            communicationStartDate: formatDate(formik.values.communicationStartDate),
            communicationEndDate: formatDate(formik.values.communicationEndDate),
            departureTime: formatDate(formik.values.departureTime),
            arrivalTime: formatDate(formik.values.arrivalTime),
            pageNumber: props.data?.pageNumber,
            maxResultPage: props.data?.maxResultPage,
            parameterToSort: props.filterOrder?.name,
            ascDesc: props.filterOrder?.order,
          },
          currentLocale
        )
      );
    }
  };

  const onReset = () => {
    formik.setValues(initialValues);

    props.setFilterOrder({ name: 'SentDateTime', order: 'DESC' });

    const initialParams = {
      isDifferedCommunication: false,
      communicationEndDate: moment().format('YYYY-MM-DD'),
      communicationStartDate: moment().subtract(10, 'days').format('YYYY-MM-DD'),
      parameterToSort: 'SentDateTime',
      ascDesc: 'DESC' as 'ASC' | 'DESC',
      pageNumber: 1,
      maxResultPage: 20,
    };

    props.setFilterValues(initialParams);

    dispatch(historicCommunicationActions.fetchHistoricCommunication(initialParams));
  };

  const inputSelectStationsData = [
    {
      id: 'stations',
      list: props.originStations?.map((station) => ({ id: station.uicStationCode, name: station.name })) || [],
    },
  ];

  const inputSelectSituationsData = [
    {
      id: 'situations',
      list:
        props.situations?.map((situation) => ({
          id: situation.id,
          name: `${situation?.situationCategory?.name} -${situation?.name}`,
        })) || [],
    },
  ];

  const inputSelectTransmittersData = [
    {
      id: 'transmitters',
      list:
        props.senders?.map((sender) => ({
          id: sender,
          name: sender,
        })) || [],
    },
  ];

  const inputSelectSendStatusData = [
    {
      id: 'sendStatus',
      list:
        props.sendStatutes?.map((status) => ({
          id: status.id,
          name: status.name,
        })) || [],
    },
  ];

  const inputSelectServiceTypesData = [
    {
      id: 'serviceTypes',
      list:
        props.actifUserServiceTypes?.map((serviceType) => ({
          id: serviceType.id,
          name: serviceType.serviceTypeName,
        })) || [],
    },
  ];

  return (
    <form onSubmit={formik.handleSubmit}>
      <FiltersCard
        title={t('filtre', 'SEARCH_FILTER_TITLE')}
        firstPart={
          <>
            <div>
              <Input
                width="150px"
                upLabel={t('filtre', 'SEARCH_FILTER_TRAIN_NUMBER')}
                name="serviceName"
                onChange={formik.handleChange}
                value={formik.values.serviceName}
              />
            </div>
            <div>
              <div>
                <UpLabel>{t('form', 'FILTER_STATION_IMPACTED')}</UpLabel>
                <InputSelect
                  onChange={(value) => formik.setFieldValue('station', value)}
                  value={formik.values.station}
                  data={inputSelectStationsData}
                  style={{ minWidth: '150px', borderRadius: '5px' }}
                  dropdownStyle={{ minWidth: '20%' }}
                  popupMatchSelectWidth
                  showSearch
                  allowClear
                />
              </div>
            </div>
            <div>
              <UpLabel>{t('form', 'FILTER_DEPARTURE_DATE')}</UpLabel>
              <DatePickerForMomentDates
                allowClear
                value={formik.values.departureTime ? moment(formik.values.departureTime) : null}
                format="DD/MM/YYYY"
                style={{ width: '150px', borderRadius: '5px' }}
                onChange={(date) => {
                  setIsNeedsDates(false);
                  formik.setFieldValue('departureTime', date);
                  if (date === null) {
                    // clear case
                    formik.setFieldValue('arrivalTime', null);
                  } else if (!formik.values.arrivalTime || moment(date).isAfter(moment(formik.values.arrivalTime))) {
                    formik.setFieldValue('arrivalTime', date);
                  }
                }}
              />
            </div>
            <div>
              <UpLabel>{t('form', 'FILTER_END_DATE')}</UpLabel>
              <DatePickerForMomentDates
                allowClear
                value={formik.values.arrivalTime ? moment(formik.values.arrivalTime) : null}
                style={{ width: '150px', borderRadius: '5px' }}
                format="DD/MM/YYYY"
                onChange={(date) => {
                  setIsNeedsDates(false);
                  formik.setFieldValue('arrivalTime', date);
                  if (date === null) {
                    // clear case
                    formik.setFieldValue('departureTime', null);
                  } else if (!formik.values.departureTime) {
                    formik.setFieldValue('departureTime', date);
                  }
                }}
                disabledDate={(current) => {
                  return current && current < moment(formik.values.departureTime);
                }}
              />
            </div>
            <div>
              <UpLabel>{t('form', 'FILTER_SENDING_DATE_FROM')}</UpLabel>
              <DatePickerForMomentDates
                allowClear
                value={formik.values.communicationStartDate ? moment(formik.values.communicationStartDate) : null}
                format="DD/MM/YYYY"
                style={{ width: '150px', borderRadius: '5px' }}
                onChange={(date) => {
                  setIsNeedsDates(false);
                  formik.setFieldValue('communicationStartDate', date);
                  if (date === null) {
                    // clear case
                    formik.setFieldValue('communicationEndDate', null);
                  } else if (
                    !formik.values.communicationEndDate ||
                    moment(date).isAfter(moment(formik.values.communicationEndDate))
                  ) {
                    formik.setFieldValue('communicationEndDate', date);
                  }
                }}
              />
            </div>
            <div>
              <UpLabel>{t('form', 'FILTER_SENDING_DATE_TO')}</UpLabel>
              <DatePickerForMomentDates
                allowClear
                disabledDate={(current) => {
                  return current && current < moment(formik.values.communicationStartDate);
                }}
                value={formik.values.communicationEndDate ? moment(formik.values.communicationEndDate) : null}
                format="DD/MM/YYYY"
                onChange={(date) => {
                  setIsNeedsDates(false);
                  formik.setFieldValue('communicationEndDate', date);
                  if (date === null) {
                    // clear case
                    formik.setFieldValue('communicationStartDate', null);
                  } else if (!formik.values.communicationStartDate) {
                    formik.setFieldValue('communicationStartDate', date);
                  }
                }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Tooltip title={t('filtre', 'SEARCH_FILTER_ONLY_PENDING')} placement="top">
                <CalendarOutlined style={{ fontSize: '25px' }} />
              </Tooltip>
              <Checkbox
                style={{ alignItems: 'center' }}
                checked={formik.values.isDifferedCommunication}
                onChange={(e) => formik.setFieldValue('isDifferedCommunication', e.target.checked)}
              />
            </div>
            <div>
              <UpLabel>{t('filtre', 'SITUATION')}</UpLabel>
              <InputSelect
                mode="multiple"
                onChange={(value) => formik.setFieldValue('situationIds', value)}
                value={formik.values.situationIds}
                data={inputSelectSituationsData}
                style={{ minWidth: '250px', borderRadius: '5px' }}
                dropdownStyle={{ minWidth: '20%' }}
                popupMatchSelectWidth
                showSearch
              />
            </div>
            <div>
              <UpLabel>{t('form', 'FILTER_TRANSMITTER')}</UpLabel>
              <InputSelect
                onChange={(value) => formik.setFieldValue('sender', value)}
                value={formik.values.sender}
                data={inputSelectTransmittersData}
                style={{ minWidth: '150px', borderRadius: '5px' }}
                dropdownStyle={{ minWidth: '20%' }}
                popupMatchSelectWidth
                showSearch
                allowClear
              />
            </div>
            <div>
              <UpLabel>{t('form', 'FILTER_STATUS')}</UpLabel>
              <InputSelect
                onChange={(value) => formik.setFieldValue('sendStatus', value)}
                value={formik.values.sendStatus}
                data={inputSelectSendStatusData}
                style={{ width: '150px', borderRadius: '5px' }}
                showSearch
              />
            </div>
            {props.actifUserServiceTypes.length > 1 && (
              <div>
                <UpLabel>{t('filtre', 'HISTO_TYPE_CIRCULATION')}</UpLabel>
                <InputSelect
                  onChange={(ids) => formik.setFieldValue('serviceTypeIDs', ids)}
                  value={formik.values.serviceTypeIDs}
                  data={inputSelectServiceTypesData}
                  style={{ minWidth: '200px', borderRadius: '5px' }}
                  dropdownStyle={{ minWidth: '20%' }}
                  showSearch
                />
              </div>
            )}
          </>
        }
        secondPart={
          <>
            <Button htmlType="submit" borderRadius="20px" width="130px">
              {t('filtre', 'SEARCH_FILTER_FILTER_BUTTON')}
            </Button>
            <StyledBlueLink onClick={() => onReset()}>{t('filtre', 'SEARCH_FILTER_RESET_BUTTON')}</StyledBlueLink>
          </>
        }
        errorPart={
          isNeedsDates && (
            <Paragraph type="danger" style={{ margin: 'auto', marginLeft: 15 }}>
              {t('filtre', 'SEARCH_FILTER_NEEDS_DATES')}
            </Paragraph>
          )
        }
        topRightPart={
          <FileExcelOutlined style={{ fontSize: 40, color: '#237f4e' }} onClick={exportHistoricCommunication} />
        }
      />
    </form>
  );
};
export default Form;
